import React, { type ChangeEvent } from "react";
import {
  Tab,
  Tabs,
  Grid,
  Paper,
  withStyles,
  WithStyles,
  createStyles,
  type Theme,
} from "@material-ui/core";
import type { GridJustification } from "@material-ui/core/Grid";

type OwnProps<T, U> = {
  classNames?: {
    paper?: string;
    parentGrid?: string;
    contentGrid?: string;
    tabs?: string;
  };
  fullWidth?: boolean;
  justify?: GridJustification;
  onTabChange: (targetValue: T, event: ChangeEvent) => void;
  selectedTab: T;
  tabs: U[];
};

type NavigationTabsImplProps<
  T extends string | number,
  U extends { id: T; label: string }
> = OwnProps<T, U> & WithStyles<typeof styles>;

function NavigationTabsImpl<
  T extends string | number,
  U extends { id: T; label: string }
>({
  classNames,
  classes,
  fullWidth = false,
  justify = "center",
  onTabChange,
  selectedTab,
  tabs,
}: NavigationTabsImplProps<T, U>) {
  const handleChangeTab = (event: ChangeEvent<unknown>, targetValue: T) => {
    onTabChange(targetValue, event as ChangeEvent);
  };

  const getTabClassName = (tabId: T) => {
    return tabId === selectedTab ? classes.selectedTab : classes.tab;
  };

  return (
    <div className={classes.root} id="navigation-tabs">
      <Paper square className={classNames?.paper}>
        <Grid
          className={classNames?.parentGrid}
          container
          direction="row"
          justify={justify}
        >
          <Grid
            className={classNames?.contentGrid}
            container
            direction="row"
            item
            justify={justify}
            lg={12}
          >
            <Tabs
              className={classNames?.tabs}
              onChange={handleChangeTab}
              scrollButtons="auto"
              TabIndicatorProps={{ className: classes.tabIndicator }}
              textColor="primary"
              value={selectedTab}
              variant={fullWidth ? "fullWidth" : "scrollable"}
            >
              {tabs.map((item) => (
                <Tab
                  className={getTabClassName(item.id)}
                  id={item.id.toString()}
                  itemID={item.id.toString()}
                  key={item.id}
                  label={item.label}
                  name={item.id.toString()}
                  TouchRippleProps={{ id: item.id }}
                  value={item.id}
                />
              ))}
            </Tabs>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      position: "fixed",
      flexGrow: 1,
      top: `75px`,
      width: "100%",
      backgroundColor: "#ccc",
      zIndex: 1000,
    },
    selectedTab: {
      color: theme.palette.primary.main,
      fontSize: "14px",
    },
    tab: {
      color: "#757575",
      fontSize: "14px",
    },
    tabIndicator: {
      backgroundColor: theme.palette.primary.main,
    },
  });

const NavigationTabs = withStyles(styles)(NavigationTabsImpl) as <
  T extends string | number,
  U extends { id: T; label: string }
>(
  props: OwnProps<T, U>
) => React.ReactElement;

export default NavigationTabs;
